import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import Breadcrumbs from "../components/Breadcrumbs";
import AvailableTimeSlotsCard from "../components/events/AvailableTimeSlotsCard";
import RecentlyCompletedSessionCard from "../components/recently-completed-session-card/RecentlyCompletedSessionCard";
import UpcomingSessionsCard from "../components/upcoming-session-card/UpcomingSessionsCard";
import LeftMenu from "../components/LeftMenu";
import { useAppSelector } from "../app/hooks";
import { selectUserProfile } from "../features/authentication/authenticationSlice";
import { selectHasUpcomingSessions, selectUpcomingSessionsInitializationStatus } from "../features/sessions/selectors/upcomingSessionsSelectors";
import { selectHasRecentlyCompletedSessions, selectRecentlyCompletedSessionsInitializationStatus } from "../features/sessions/selectors/recentlyCompletedSessionsSelectors";
import { RootState } from "../app/store";
import { filterUpcomingTimeSlots } from "../features/utils/timeSlotUtils";

const HomePage = () => {
  const { t } = useTranslation();
  const userProfile = useAppSelector(selectUserProfile);
  const firstName = userProfile?.name?.split(" ")[0] || "";

  const hasUpcoming = useAppSelector(selectHasUpcomingSessions);
  const hasCompleted = useAppSelector(selectHasRecentlyCompletedSessions);
  const availableTimeSlots = useAppSelector(
    (state: RootState) => state.sessions.timeSlots.availableTimeSlots
  );
  const hasTimeSlots = useMemo(
    () => filterUpcomingTimeSlots(availableTimeSlots).length > 0,
    [availableTimeSlots]
  );

  // Wait for all data sources to finish loading before reordering
  const upcomingInit = useAppSelector(selectUpcomingSessionsInitializationStatus);
  const completedInit = useAppSelector(selectRecentlyCompletedSessionsInitializationStatus);
  const timeSlotsInit = useAppSelector(
    (state: RootState) => state.sessions.timeSlots.initializationStatus
  );
  const allInitialized =
    upcomingInit === "initialized" &&
    completedInit === "initialized" &&
    timeSlotsInit === "initialized";

  // Cards in default order, each with a hasData flag
  const cards = useMemo(() => {
    const items = [
      { key: "upcoming", hasData: hasUpcoming, component: <UpcomingSessionsCard /> },
      { key: "completed", hasData: hasCompleted, component: <RecentlyCompletedSessionCard /> },
      { key: "timeSlots", hasData: hasTimeSlots, component: <AvailableTimeSlotsCard /> },
    ];
    // Only reorder once all data has loaded to avoid layout shift
    if (!allInitialized) return items;
    return [...items].sort((a, b) => Number(b.hasData) - Number(a.hasData));
  }, [hasUpcoming, hasCompleted, hasTimeSlots, allInitialized]);

  return (
    <div className="flex h-full gap-4">
      {/* Left Side */}
      <LeftMenu />
      <div className="w-full p-4 pb-10 lg:px-10">
        <div className="lg:max-w-[694px]">
          <Breadcrumbs />
          <div className="space-y-4 py-6 lg:space-y-6">
            <h1>{t("home.welcomeBack", { firstName })}</h1>
            {cards.map((card) => (
              <div key={card.key}>{card.component}</div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
